/**
 * Web Sign in with Apple for RE-AUTHENTICATION on browsers (not for regular
 * sign-in).
 *
 * The regular web Apple sign-in path is a full-page redirect through
 * Supabase's `/auth/v1/authorize` (see authService.signInWithApple). That
 * flow is unusable for the account-deletion re-auth step because it replaces
 * the current session and navigates away from the delete page — the user
 * would come back to a fresh session with all of the delete-page state gone.
 *
 * This module uses Apple's own JS SDK (AppleID.auth) with `usePopup: true`
 * instead: the OS/Apple sign-in sheet opens in a popup, returns an Apple
 * `id_token` in-page, and closes itself. The id_token is then POSTed to
 * /api/auth/account[/permanent] as the reauth credential, exactly like the
 * native iOS path does with the token from the ASAuthorization sheet.
 * Backend verification (services/auth/account-reauth.service.ts) is
 * provider-agnostic — signInWithIdToken checks JWKS + audience + nonce and
 * the caller confirms `user.id` matches the current session's user.
 *
 * ─── Config it depends on (APPLE_OAUTH_SETUP.md §1/§2) ──────────────────────
 *
 * `clientId` is the Services ID `com.gotradeloop.web` — the same `aud`
 * Supabase's Apple provider already trusts (it is listed first in "Client
 * IDs" in the Supabase provider config).
 *
 * `redirectURI` MUST be a URL registered against the Services ID in Apple
 * Developer Portal → Identifiers → Services IDs → Trade Loop Web →
 * Configure → Return URLs. Prior to this file only Supabase's callback was
 * registered, which is why Apple web reauth was previously deferred. Each
 * new browser origin the app runs on (production, staging, any dev domain)
 * must be added there — Apple accepts only `https://` URLs, so
 * `http://localhost:*` cannot be registered and this path cannot be tested
 * against local dev. Use a staging domain, or test on prod.
 *
 * The nonce contract mirrors the native path exactly: `hashed` (SHA-256 of a
 * random 32-byte string) goes to Apple, `raw` goes to the backend. Reversing
 * them fails signInWithIdToken with a generic 401 that looks like a bad
 * audience, which is the Apple twin of the Google id_token nonce trap.
 */

/** Apple JS SDK URL. Same version tag Sign in with Apple's docs recommend. */
const APPLE_JS_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

/** Services ID — must match Supabase provider's "Client IDs" list. */
const APPLE_SERVICES_ID =
  (import.meta.env.VITE_APPLE_SERVICES_ID as string | undefined) || 'com.gotradeloop.web';

/**
 * Minimal Apple JS SDK surface. The full types are not on npm in a usable
 * shape and pulling `@types/apple-signin-auth` for two calls is not worth it.
 */
interface AppleAuthorizationResponse {
  authorization: {
    id_token: string;
    code: string;
    state?: string;
  };
  // Apple returns `user` on the FIRST authorization only (see
  // src/docs/APPLE_OAUTH_SETUP.md §5). We do not use it here because reauth
  // never happens on the first authorization by definition — the account
  // already exists and the name is already stored server-side.
  user?: {
    name?: { firstName?: string; lastName?: string };
    email?: string;
  };
}

interface AppleAuthInit {
  init: (params: {
    clientId: string;
    scope: string;
    redirectURI: string;
    state?: string;
    nonce?: string;
    usePopup?: boolean;
  }) => void;
  signIn: (params?: {
    state?: string;
    nonce?: string;
  }) => Promise<AppleAuthorizationResponse>;
}

declare global {
  interface Window {
    AppleID?: {
      auth?: AppleAuthInit;
    };
  }
}

let loadPromise: Promise<void> | null = null;

/**
 * Insert the Apple JS SDK script tag once and resolve when the global is
 * available. Same idempotent-with-retry-on-failure pattern as
 * webGoogleAuth.ts — Apple's CDN has been unreliable enough historically
 * (§ APPLE_OAUTH_SETUP.md) that a retry after network failure needs to work.
 */
export function ensureAppleIdentityLoaded(): Promise<void> {
  if (window.AppleID?.auth) return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${APPLE_JS_SRC}"]`);
    const script = existing ?? document.createElement('script');
    if (!existing) {
      script.src = APPLE_JS_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const onLoad = () => {
      if (window.AppleID?.auth) resolve();
      else reject(new Error('Apple sign-in JS loaded but window.AppleID.auth is missing.'));
    };
    const onError = () => {
      loadPromise = null;
      reject(new Error('Failed to load Apple sign-in JS.'));
    };

    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', onError, { once: true });

    if (window.AppleID?.auth) resolve();
  }).catch((error) => {
    loadPromise = null;
    throw error;
  });

  return loadPromise;
}

const toHex = (bytes: Uint8Array): string =>
  Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

async function generateNoncePair(): Promise<{ raw: string; hashed: string }> {
  const webCrypto = globalThis.crypto;
  if (!webCrypto?.subtle || !webCrypto.getRandomValues) {
    throw new Error('This browser cannot generate a secure sign-in nonce (WebCrypto unavailable).');
  }
  const raw = toHex(webCrypto.getRandomValues(new Uint8Array(32)));
  const digest = await webCrypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
  return { raw, hashed: toHex(new Uint8Array(digest)) };
}

/**
 * User dismissed the Apple sheet. Apple's JS SDK rejects with an object like
 * `{ error: 'popup_closed_by_user' }` or `{ error: 'user_cancelled_authorize' }`
 * depending on how they dismissed it. The caller silences all of these.
 */
export function isAppleWebUserCancellation(error: unknown): boolean {
  const code = (error as { error?: string } | null)?.error;
  if (typeof code !== 'string') return false;
  return (
    code === 'popup_closed_by_user' ||
    code === 'user_cancelled_authorize' ||
    code === 'user_trigger_new_signin_flow'
  );
}

/**
 * Open Apple's popup and return the assertion to POST to the backend.
 *
 * The `raw` nonce is returned alongside the id_token so the caller can POST
 * both, mirroring the native path's contract.
 *
 * Throws:
 *  - `{ error: 'popup_closed_by_user' }` shape when the user cancels. Callers
 *    should test with `isAppleWebUserCancellation()` and stay silent.
 *  - Any other error means the SDK failed to load OR Apple rejected the
 *    request (typically an unregistered `redirectURI` — see the module
 *    comment). Surface it verbatim; the raw message is the fastest way to
 *    diagnose the developer-portal config gap.
 */
export async function getAppleIdTokenWeb(): Promise<{ idToken: string; nonce: string }> {
  await ensureAppleIdentityLoaded();

  const appleAuth = window.AppleID?.auth;
  if (!appleAuth) {
    throw new Error('Apple sign-in is unavailable.');
  }

  const { raw, hashed } = await generateNoncePair();

  // `redirectURI` must be a Return URL registered against the Services ID in
  // Apple Developer Portal. `usePopup: true` means Apple never actually
  // navigates the browser TO it — Apple validates it server-side, returns the
  // assertion via the SDK's Promise, and the popup closes itself. So this
  // does NOT need a receiver page on our end, just a matching entry in
  // Apple's config.
  const redirectURI = window.location.origin;

  appleAuth.init({
    clientId: APPLE_SERVICES_ID,
    scope: 'name email',
    redirectURI,
    usePopup: true,
    nonce: hashed,
  });

  const response = await appleAuth.signIn({ nonce: hashed });
  const idToken = response?.authorization?.id_token;
  if (!idToken) {
    throw new Error('Apple did not return an identity token.');
  }
  return { idToken, nonce: raw };
}
